// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import ViewField from '@react/components/elements/ViewField'
import { fetchCollateral } from '@react/fetchers/collateralFetchers'
import { fetchDefiFeeAccrual, postDefiFeeAccrualRepayment } from '@react/fetchers/defiFetchers'
import { useOtChainState, useOtSelector } from '@react/redux/store'
import { PoolControllerDynamicAbi, PoolDynamicAbi } from '@shared/Abis'
import { CollateralSetCTO, DefiCollateralCTO } from '@shared/CollateralSetCTO'
import { FeeAccrualAccountCTO, FeeAccrualCTO, FeeAccrualCTOAccountSchema } from '@shared/FeeAccrualCTO'
import { logOT } from '@shared/loggingShared'
import { PoolDynamicOverviewCTO } from '@shared/PoolDynamicCTO'
import { ETH_ADDR_ZERO, EthAddr } from '@shared/SchemaTypes'
import { PoolDynamic } from '@typechain-types/contracts/PoolDynamic'
import { PoolControllerDynamic } from '@typechain-types/index'
import { Contract } from 'ethers'
import React, { useEffect, useState } from 'react'

export default function DefiFeeForm(props: { poolAddr: EthAddr }): React.ReactElement {
  const { poolAddr } = props
  const theme = useTheme()
  const [feeCTO, setFeeCTO] = useState<FeeAccrualCTO | undefined>(undefined)
  const [collateralSetCTO, setCollateralSetCTO] = useState<CollateralSetCTO | undefined>(undefined)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [finished, setFinished] = useState(false)
  const [burnChecked, setBurnChecked] = useState<Record<EthAddr, boolean>>({})
  const [mintChecked, setMintChecked] = useState<Record<EthAddr, boolean>>({})
  const [feeAccrualChecked, setFeeAccrualChecked] = useState<Record<EthAddr, boolean>>({})
  const poolOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO]
  ) as PoolDynamicOverviewCTO
  const {
    chainConfiguration: { providerReadOnly },
    stageConfiguration: { serverApiBaseUrl, restAPIKey },
    userConnection: { signer },
    cognitoUser,
  } = useOtChainState()
  useEffect(() => {
    if (poolAddr) {
      fetchDefiFeeAccrual(poolAddr, serverApiBaseUrl, restAPIKey)
        .then((feeCTO) => {
          setFeeCTO(feeCTO)
          logOT.info('Collateral set fetching...', poolAddr)
          return fetchCollateral(serverApiBaseUrl, restAPIKey, poolAddr)
        })
        .then((collateralSetCTO) => {
          logOT.info('collateralSetCTO', collateralSetCTO)
          setCollateralSetCTO(collateralSetCTO)
        })

        .catch((error) => {
          console.error('error fetching defi fee accrual', error)
        })
    }
  }, [poolAddr])

  if (!feeCTO) {
    return <div>Loading...</div>
  }
  const doBurn = async (feeAccrualAccountCTO: FeeAccrualAccountCTO, poolControllerContract: PoolControllerDynamic) => {
    const tx = await poolControllerContract
      .connect(signer)
      .addAdjustmentAmount(feeAccrualAccountCTO.accountAddr, BigInt(feeAccrualAccountCTO.feeAccrual), 0)
    const tx3 = await tx.wait()
    const b = { ...burnChecked, [feeAccrualAccountCTO.accountAddr]: true }
    setBurnChecked(b)

    return tx3
  }
  const doMint = async (feeAccrualAccountCTO: FeeAccrualAccountCTO, poolControllerContract: PoolControllerDynamic) => {
    const feeVaultAddress = (collateralSetCTO?.collateral as DefiCollateralCTO[])[0]?.feeVaultWalletAddr

    logOT.info('addAdjustmentAmount', feeVaultAddress, BigInt(feeAccrualAccountCTO.feeAccrual))
    const tx = await poolControllerContract
      .connect(signer)
      .addAdjustmentAmount(feeVaultAddress, 0, BigInt(feeAccrualAccountCTO.feeAccrual))
    await tx.wait()
    logOT.info('do mint success')
    const b = { ...mintChecked, [feeAccrualAccountCTO.accountAddr]: true }
    setMintChecked(b)
    return
  }
  const doAccrueFees = async (feeAccrualAccountCTO: FeeAccrualAccountCTO) => {
    const updatedFeeAccrualAccountCTO: FeeAccrualAccountCTO = { ...feeAccrualAccountCTO, feeAccrual: '0' }
    await postDefiFeeAccrualRepayment(updatedFeeAccrualAccountCTO, serverApiBaseUrl, restAPIKey, cognitoUser)
    setFeeAccrualChecked({ ...feeAccrualChecked, [feeAccrualAccountCTO.accountAddr]: true })
  }
  const performAccrueFees = () => {
    setIsSubmitted(true)
    const performAccrueFeesAsync = async () => {
      const poolContract = new Contract(poolAddr, PoolDynamicAbi, providerReadOnly) as unknown as PoolDynamic
      const poolControllerContract = new Contract(
        await poolContract.poolControllerDynamic(),
        PoolControllerDynamicAbi,
        providerReadOnly
      ) as unknown as PoolControllerDynamic

      const feeAccrualAccountCTOs = feeCTO.feeAccrualAccountCTOs.filter(
        (feeAccrualAccountCTO) => Number(feeAccrualAccountCTO.feeAccrual) > 0
      )
      for (const feeAccrualAccountCTO of feeAccrualAccountCTOs) {
        const burnTx = await doBurn(feeAccrualAccountCTO, poolControllerContract)
        logOT.info('burnTx', burnTx)
        const mintTx = await doMint(feeAccrualAccountCTO, poolControllerContract)
        logOT.info('mintTx', mintTx)
        await doAccrueFees(feeAccrualAccountCTO)
      }
      setFinished(true)
    }
    performAccrueFeesAsync()
      .catch((error) => {
        console.error('error performing accrue fees', error)
        setIsSubmitted(false)
      })
      .finally(() => {
        setIsSubmitted(false)
      })
  }
  return (
    <Box
      data-testid="DefiFeeForm"
      sx={{
        width: 760,
        maxWidth: 760,
        backgroundColor: theme.palette.background.paper,
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Typography variant="h6">
        Fees are transferred at Pool Tokens to the fee vault wallet{' '}
        {(collateralSetCTO?.collateral?.[0] as DefiCollateralCTO)?.feeVaultWalletAddr}
      </Typography>
      <Typography variant="body1">
        The following fees are due to be accrued. Please select the actions you want to perform for each account.
      </Typography>
      {feeCTO.feeAccrualAccountCTOs
        .filter((feeAccrualAccountCTO) => Number(feeAccrualAccountCTO.feeAccrual) > 0)
        .map((feeAccrualAccountCTO) => (
          <Box
            key={feeAccrualAccountCTO.accountAddr}
            sx={{ width: 760, maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <Box
              data-testid="DefiFeeForm-rowBox"
              key={feeAccrualAccountCTO.accountAddr}
              sx={{ width: 760, maxWidth: 760, display: 'flex', flexDirection: 'row', gap: 2 }}
            >
              <Box sx={{ width: 380, maxWidth: 380 }}>
                <ViewField
                  dataObject={feeAccrualAccountCTO}
                  liquidityTokenSymbol={poolOverviewCTO?.liquidityTokenSymbol}
                  poolTokenSymbol={poolOverviewCTO?.symbol}
                  fieldSchema={FeeAccrualCTOAccountSchema.describe().fields.accountAddr}
                />
              </Box>
              <Box sx={{ width: 380, maxWidth: 380 }}>
                <ViewField
                  dataObject={feeAccrualAccountCTO}
                  liquidityTokenSymbol={poolOverviewCTO?.liquidityTokenSymbol}
                  poolTokenSymbol={poolOverviewCTO?.symbol}
                  fieldSchema={FeeAccrualCTOAccountSchema.describe().fields.feeAccrual}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                direction: 'row',

                marginBottom: 3,
              }}
            >
              <Checkbox
                checked={burnChecked[feeAccrualAccountCTO.accountAddr] ?? false}
                sx={{
                  color: '#888888',
                  '&.Mui-checked': { color: '#e91e63' },
                  py: 0.5,
                }}
              />
              <Typography variant="body1">Burn</Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                direction: 'row',

                marginBottom: 3,
              }}
            >
              <Checkbox
                checked={mintChecked[feeAccrualAccountCTO.accountAddr] ?? false}
                sx={{
                  color: '#888888',
                  '&.Mui-checked': { color: '#e91e63' },
                  py: 0.5,
                }}
              />
              <Typography variant="body1">Mint</Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                direction: 'row',

                marginBottom: 3,
              }}
            >
              <Checkbox
                checked={feeAccrualChecked[feeAccrualAccountCTO.accountAddr] ?? false}
                sx={{
                  color: '#888888',
                  '&.Mui-checked': { color: '#e91e63' },
                  py: 0.5,
                }}
              />
              <Typography variant="body1">Fee Accrual</Typography>
            </Box>
          </Box>
        ))}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}></Box>
      {finished && (
        <Box
          sx={{
            width: '90%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginBottom: 3,
            gap: 8,
          }}
        >
          <Button
            variant="contained"
            disabled={isSubmitted}
            onClick={() => {
              const event = new Event('modalClose')
              window.dispatchEvent(event)
            }}
          >
            <Typography variant="buttonBold">Close</Typography>
          </Button>
        </Box>
      )}
      {!finished && (
        <Box
          sx={{
            width: '90%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginBottom: 3,
            gap: 8,
          }}
        >
          <Button
            variant="contained"
            disabled={isSubmitted}
            onClick={() => {
              const event = new Event('modalClose')
              window.dispatchEvent(event)
            }}
          >
            <Typography variant="buttonBold">Cancel</Typography>
          </Button>
          <Button
            disabled={isSubmitted || !feeCTO || !collateralSetCTO}
            variant="contained"
            onClick={performAccrueFees}
          >
            <Typography variant="buttonBold">Accrue Fees</Typography>
          </Button>
        </Box>
      )}
    </Box>
  )
}
